// Экран «Фокус»: какая задача сейчас главная и почему.
//
// Логика зеркалит app/reminders.py::pick_focus_task — бот утром называет ту же
// задачу, что покажет мини-апп. Меняешь порядок здесь — поменяй и там.
//
// Порядок: выбранная утром на сегодня → важная → просроченная (давние первыми) →
// дальше в том порядке, в каком задачи стоят в списке «Сегодня». Список человек
// сортирует руками, и «Дальше» на карточке должно совпадать с тем, что он видит
// первым под ней. У бота ручного порядка нет, там последний ключ — срок.
// «Позже сегодня» не меняет данных на сервере, поэтому такие задачи пропускаются
// по списку skipped, который живёт только в текущей сессии.

export const MORNING_PICK_LIMIT = 3;
export const SPLIT_SUGGEST_AFTER = 2; // на третьем «Позже» предлагаем разбить задачу
export const DAY_CLOSE_HOUR = 19;

export function dayKey(date) {
  const d = new Date(date);
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 10);
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function dueDay(task) {
  if (!task || !task.due_date) return null;
  const d = new Date(task.due_date);
  return isNaN(d) ? null : startOfDay(d);
}

export function isPicked(task, today) {
  return !!task.focus_on && task.focus_on === dayKey(today);
}

export function isOverdue(task, today) {
  const d = dueDay(task);
  return !!d && d < startOfDay(today);
}

function rank(task, today) {
  const late = isOverdue(task, today);
  return [
    isPicked(task, today) ? 0 : 1,
    task.important ? 0 : 1,
    late ? 0 : 1,
    late ? new Date(task.due_date).getTime() : 0,
  ];
}

function compareRanks(a, b) {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

// Все кандидаты в порядке очереди; первый — главная задача. Сортировка
// устойчивая: при равных ключах сохраняется порядок входного списка.
export function focusQueue(todayTasks, today = new Date(), skipped = []) {
  const skip = new Set(skipped);
  const pending = (todayTasks || []).filter(t => t.status !== 'done');
  const sorted = pending
    .map(t => ({ t, r: rank(t, today) }))
    .sort((a, b) => compareRanks(a.r, b.r))
    .map(x => x.t);
  const fresh = sorted.filter(t => !skip.has(t.id));
  // Отложили всё — возвращаемся к началу очереди, а не показываем пустоту.
  return fresh.length ? fresh : sorted;
}

export function pickFocus(todayTasks, today = new Date(), skipped = []) {
  return focusQueue(todayTasks, today, skipped)[0] || null;
}

// Почему выбрана именно эта задача — подпись под заголовком карточки.
export function focusReason(task, today = new Date()) {
  if (!task) return '';
  if (isPicked(task, today)) return 'Вы сами выбрали её главной на сегодня.';
  const late = isOverdue(task, today);
  if (task.important && late) return 'Важная и уже просрочена — начните с неё.';
  if (task.important) return 'Помечена важной, срок сегодня.';
  if (late) return 'Висит дольше остальных.';
  return 'Ближайшая по сроку.';
}

export function focusTags(task, today = new Date()) {
  if (!task) return [];
  const tags = [];
  if (isPicked(task, today)) tags.push('главное');
  if (task.important) tags.push('важно');
  if (isOverdue(task, today)) {
    const days = Math.round((startOfDay(today) - dueDay(task)) / 86400000);
    tags.push(days === 1 ? 'просрочено со вчера' : `просрочено ${days} дн.`);
  }
  return tags;
}

// Дата для «Позже»: завтра / через неделю, в формате, который ждёт бэкенд.
export function postponeDate(option, today = new Date()) {
  const base = startOfDay(today);
  if (option === 'tomorrow') base.setDate(base.getDate() + 1);
  else if (option === 'week') base.setDate(base.getDate() + 7);
  else return null;
  return dayKey(base);
}

export function shouldSuggestSplit(task) {
  return !!task && (task.postpone_count || 0) >= SPLIT_SUGGEST_AFTER;
}

// Утренний выбор показываем один раз в день, если выбирать есть из чего и
// человек ещё ничего не выбрал (например, с другого устройства).
export function needsMorningPick(todayTasks, today, alreadyAsked) {
  if (alreadyAsked) return false;
  const pending = (todayTasks || []).filter(t => t.status !== 'done');
  if (pending.length < 2) return false;
  return !pending.some(t => isPicked(t, today));
}

export function needsDayClose(todayTasks, now, alreadyClosed) {
  if (alreadyClosed) return false;
  if (new Date(now).getHours() < DAY_CLOSE_HOUR) return false;
  return (todayTasks || []).some(t => t.status !== 'done');
}

export function plural(n, one, few, many) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

// ---------- пустой день ----------
// Логика зеркалит app/reminders.py::pick_empty_day_candidates: ближайшие планы
// на неделю → важные идеи → самые старые идеи (id растёт с созданием).
export const EMPTY_DAY_PLAN_HORIZON = 7;

export function emptyDayCandidates(tasks, today = new Date(), skipped = [], limit = 6) {
  const skip = new Set(skipped);
  const base = startOfDay(today);
  const horizon = new Date(base);
  horizon.setDate(horizon.getDate() + EMPTY_DAY_PLAN_HORIZON);
  const pending = (tasks || []).filter(t => t.status !== 'done' && !skip.has(t.id));
  const plans = pending
    .filter(t => { const d = dueDay(t); return d && d > base && d <= horizon; })
    .sort((a, b) => dueDay(a) - dueDay(b) || a.id - b.id)
    .map(t => ({ task: t, source: 'plan' }));
  const ideas = pending.filter(t => !dueDay(t)).sort((a, b) => a.id - b.id);
  const important = ideas.filter(t => t.important).map(t => ({ task: t, source: 'important' }));
  const rest = ideas.filter(t => !t.important).map(t => ({ task: t, source: 'idea' }));
  return [...plans, ...important, ...rest].slice(0, limit);
}

export function candidateLabel(candidate) {
  if (!candidate) return '';
  if (candidate.source === 'plan') {
    const d = dueDay(candidate.task);
    return `из планов · на ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`;
  }
  if (candidate.source === 'important') return 'важная идея';
  return 'давно лежит в идеях';
}

// Подсказки для быстрого ввода: что человек уже делал не раз и чего сейчас нет в работе.
// Сделанное сегодня не предлагаем — «добавить то, что только что закрыл» звучит странно.
export function frequentTitles(tasks, limit = 4, today = new Date()) {
  const todayKey = dayKey(today);
  const pendingTitles = new Set((tasks || []).filter(t => t.status !== 'done').map(t => (t.description || '').trim().toLowerCase()));
  const counts = new Map();
  (tasks || [])
    .filter(t => t.status === 'done' && !(t.completed_at && dayKey(new Date(t.completed_at)) === todayKey))
    .sort((a, b) => b.id - a.id)
    .forEach(t => {
      const title = (t.description || '').trim();
      const key = title.toLowerCase();
      if (!title || title.length > 40 || pendingTitles.has(key)) return;
      const cur = counts.get(key) || { title, n: 0, order: counts.size };
      cur.n += 1;
      counts.set(key, cur);
    });
  return [...counts.values()]
    .sort((a, b) => b.n - a.n || a.order - b.order)
    .slice(0, limit)
    .map(x => x.title);
}

export function tomorrowTasks(tasks, today = new Date()) {
  const t = startOfDay(today);
  t.setDate(t.getDate() + 1);
  return (tasks || []).filter(x => x.status !== 'done' && dueDay(x) && dueDay(x).getTime() === t.getTime());
}

// Разбор завалов: предлагаем, когда просроченного накопилось столько, что список
// перестаёт помогать и начинает давить.
export const TRIAGE_THRESHOLD = 5;

export function overdueTasks(tasks, today = new Date()) {
  return (tasks || [])
    .filter(t => t.status !== 'done' && isOverdue(t, today))
    .sort((a, b) => dueDay(a) - dueDay(b) || a.id - b.id);
}

export function needsTriage(tasks, today = new Date()) {
  return overdueTasks(tasks, today).length >= TRIAGE_THRESHOLD;
}

export function daysOverdue(task, today = new Date()) {
  const d = dueDay(task);
  return d ? Math.round((startOfDay(today) - d) / 86400000) : 0;
}

// «Протухание»: почти всё, что вообще делают, делают в первые 11 дней. Задача,
// просроченная дольше, сама уже не сдвинется — честнее решить её судьбу:
// первый шаг, в идеи или отпустить. Идеи (без даты) не протухают.
// Тот же порог — на бэкенде (app/engagement.py::STALE_DAYS).
export const STALE_DAYS = 12;

export function staleTasks(tasks, today = new Date()) {
  return overdueTasks(tasks, today).filter(t => daysOverdue(t, today) >= STALE_DAYS);
}
